// src/layouts/annotator/exams/index.js
// Annotator — My Exams (A1 or A2) with tabs: Pending | In Progress | Completed | Needs Revision

import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "hooks/useTranslation";
import { DateTimeCell } from "components/DateCells";

import { getStudentExamsForAnnotator } from "services/sampleService";

const ANNOTATION_ROUTE = "/annotator/exams/annotation";

function ExamIdCell({ row, onOpen }) {
  const examId = row?.original?.exam_id || "";
  return (
    <Link component="button" variant="body2" onClick={() => onOpen(examId)} underline="hover">
      {examId}
    </Link>
  );
}
ExamIdCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      exam_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
  }).isRequired,
  onOpen: PropTypes.func.isRequired,
};

function QaCommentCell({ value }) {
  const text = value || "—";
  if (text === "—") return text;
  return (
    <Tooltip title={text} placement="top-start">
      <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
        {text}
      </Typography>
    </Tooltip>
  );
}
QaCommentCell.propTypes = {
  value: PropTypes.string,
};

function AnnotatorExamsIndex() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getAnnotatorId = () => {
    const candidates = [localStorage.getItem("user_id")].filter(Boolean);
    if (candidates.length) {
      const v = candidates[0];
      const n = Number(v);
      return Number.isNaN(n) ? v : n;
    }
    return null;
  };
  const annotatorId = getAnnotatorId();

  const [loading, setLoading] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [tab, setTab] = useState(0);

  const handleOpenExam = useCallback(
    (examId) => {
      const url = `${ANNOTATION_ROUTE}?exam_id=${encodeURIComponent(examId)}`;
      navigate(url);
    },
    [navigate]
  );

  const columnsPendingInProgress = useMemo(
    () => [
      {
        Header: t("annotator.exams.table.columns.sessionId", "Session ID"),
        accessor: "exam_id",
        align: "left",
        Cell: (p) => <ExamIdCell row={p.row} onOpen={handleOpenExam} />,
      },
      {
        Header: t("annotator.exams.table.columns.assignedOn", "Assigned On"),
        accessor: "my_assigned_on",
        align: "left",
        Cell: DateTimeCell,
      },
    ],
    [t, handleOpenExam]
  );

  const columnsSubmitted = useMemo(
    () => [
      {
        Header: t("annotator.exams.table.columns.sessionId", "Session ID"),
        accessor: "exam_id",
        align: "left",
        Cell: (p) => <ExamIdCell row={p.row} onOpen={handleOpenExam} />,
      },
      {
        Header: t("annotator.exams.table.columns.submittedOn", "Submitted On"),
        accessor: "my_submitted_on",
        align: "left",
        Cell: DateTimeCell,
      },
    ],
    [t, handleOpenExam]
  );

  const columnsNeedsRevision = useMemo(
    () => [
      {
        Header: t("annotator.exams.table.columns.sessionId", "Session ID"),
        accessor: "exam_id",
        align: "left",
        Cell: (p) => <ExamIdCell row={p.row} onOpen={handleOpenExam} />,
      },
      {
        Header: t("annotator.exams.table.columns.qaFeedback", "QA feedback"),
        accessor: "qa_comment",
        align: "left",
        Cell: QaCommentCell,
      },
      {
        Header: t("annotator.exams.table.columns.reviewedBy", "Reviewed by"),
        accessor: "qa_reviewed_by_name",
        align: "left",
      },
      {
        Header: t("annotator.exams.table.columns.reviewedOn", "Reviewed on"),
        accessor: "qa_reviewed_on",
        align: "left",
        Cell: DateTimeCell,
      },
    ],
    [t, handleOpenExam]
  );

  const columns =
    tab === 2 ? columnsSubmitted : tab === 3 ? columnsNeedsRevision : columnsPendingInProgress;

  const fetchMyExams = useCallback(async () => {
    if (!annotatorId) {
      console.error("Annotator ID not found; ensure user is logged in and userId is available.");
      setAllRows([]);
      return;
    }
    try {
      setLoading(true);
      const resp = await getStudentExamsForAnnotator(annotatorId, { limit: 1000 });

      const mapped = (resp.data || []).map((e) => {
        const isA1 = String(e.annotator1_id ?? "") === String(annotatorId);
        const isA2 = String(e.annotator2_id ?? "") === String(annotatorId);

        const myStatus = isA1 ? e.annotator1_status : isA2 ? e.annotator2_status : e.status;
        const assignedRaw = isA1
          ? e.annotator1_assigned_on
          : isA2
          ? e.annotator2_assigned_on
          : null;
        const submittedRaw = isA1
          ? e.annotator1_submitted_on
          : isA2
          ? e.annotator2_submitted_on
          : null;

        return {
          exam_id: e.exam_id ?? "",
          student_id: e.student_id ?? "",
          student_name: e.student_name ?? "",
          my_status: myStatus || "",
          my_assigned_on: assignedRaw ?? "",
          my_submitted_on: submittedRaw ?? "",
          qa_status: e.qa_status || "",
          qa1_status: e.qa1_status || "",
          qa2_status: e.qa2_status || "",
          qa_comment: e.qa_comment || "",
          qa1_comment: e.qa1_comment || "",
          qa2_comment: e.qa2_comment || "",
          qa_reviewed_by_name: e.qa_reviewed_by_name || "—",
          qa_reviewed_on: e.qa_reviewed_on ?? "",
          status: e.status ?? "",
          annotator1_id: e.annotator1_id ?? null,
          annotator2_id: e.annotator2_id ?? null,
          annotator1_name: e.annotator1_name ?? (e.annotator1_id ? `#${e.annotator1_id}` : ""),
          annotator2_name: e.annotator2_name ?? (e.annotator2_id ? `#${e.annotator2_id}` : ""),
          annotator1_status: e.annotator1_status ?? "",
          annotator2_status: e.annotator2_status ?? "",
          start_time: e.start_time ?? "",
          end_time: e.end_time ?? "",
          my_annotator_slot: isA1 ? 1 : isA2 ? 2 : null,
        };
      });

      setAllRows(mapped);
    } catch (err) {
      console.error("fetchMyExams error:", err);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [annotatorId]);

  useEffect(() => {
    fetchMyExams();
  }, [fetchMyExams]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchMyExams();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [fetchMyExams]);

  const isRejected = (row) => {
    if (row.my_annotator_slot === 1) return row.qa1_status === "Rejected";
    if (row.my_annotator_slot === 2) return row.qa2_status === "Rejected";
    return row.qa_status === "Rejected";
  };

  const pendingRows = useMemo(
    () => allRows.filter((r) => (r.my_status || "").toLowerCase() === "pending" && !isRejected(r)),
    [allRows]
  );
  const progressRows = useMemo(
    () =>
      allRows.filter((r) => (r.my_status || "").toLowerCase() === "in progress" && !isRejected(r)),
    [allRows]
  );
  const submittedRows = useMemo(() => {
    const s = (v) => (v || "").toLowerCase();
    return allRows.filter(
      (r) => !isRejected(r) && (s(r.my_status) === "in review" || s(r.my_status) === "completed")
    );
  }, [allRows]);
  const needsRevisionRows = useMemo(() => allRows.filter((r) => isRejected(r)), [allRows]);

  const currentRows =
    tab === 0
      ? pendingRows
      : tab === 1
      ? progressRows
      : tab === 2
      ? submittedRows
      : needsRevisionRows;

  const tabDefs = [
    {
      key: 0,
      label: t("annotator.exams.tabs.pending", "Pending"),
      count: pendingRows.length,
      barColor: "#e53935",
    },
    {
      key: 1,
      label: t("annotator.exams.tabs.inProgress", "In Progress"),
      count: progressRows.length,
      barColor: "#1d57dd",
    },
    {
      key: 2,
      label: t("annotator.exams.tabs.submitted", "Completed"),
      count: submittedRows.length,
      barColor: "#43a047",
    },
    {
      key: 3,
      label: t("annotator.exams.tabs.needsRevision", "Needs revision"),
      count: needsRevisionRows.length,
      barColor: "#fb8c00",
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                p: 2,
                border: "1px solid #e0e0e0",
                boxShadow: 2,
                borderRadius: 2,
                backgroundColor: "#fff",
              }}
            >
              <Box
                role="tablist"
                aria-label={t("annotator.exams.tabs.ariaLabel", "annotator exam status tabs")}
                display="flex"
                gap={1}
                mb={2}
                flexWrap="wrap"
                alignItems="center"
              >
                <MDTypography mr={1} fontWeight="bold">
                  {t("annotator.exams.title", "Status")} :
                </MDTypography>

                {tabDefs.map((tabDef) => {
                  const isActive = tab === tabDef.key;

                  return (
                    <Button
                      key={tabDef.key}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setTab(tabDef.key)}
                      variant="outlined"
                      sx={{
                        borderRadius: 1,
                        minWidth: 120,
                        textTransform: "none",
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        color: "#111",
                        border: "1px solid #ccc",
                        background: "#fff",
                        padding: "8px 18px",
                        boxShadow: isActive ? `0 4px 0 0 ${tabDef.barColor}` : "0 4px 0 0 #ccc",
                        transition: "all 120ms ease",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: isActive ? `0 6px 0 0 ${tabDef.barColor}` : "0 6px 0 0 #bbb",
                        },
                      }}
                    >
                      {tabDef.label}
                      {tabDef.key === 3 && needsRevisionRows.length > 0 && (
                        <Chip
                          label={t("qa.status.rejected", "Rejected")}
                          color="error"
                          size="small"
                          sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                        />
                      )}
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          fontWeight: 600,
                          color: "#555",
                          background: "#f2f4f6",
                          px: 1,
                          borderRadius: "6px",
                          fontSize: "0.85rem",
                        }}
                      >
                        {tabDef.count}
                      </Box>
                    </Button>
                  );
                })}
              </Box>

              <Card
                sx={{
                  boxShadow: 0,
                  borderRadius: 2,
                  overflow: "hidden",
                  backgroundColor: "#f9fafe",
                  border: "1px solid #e0e0e0",
                }}
              >
                <MDBox>
                  <DataTable
                    table={{ columns, rows: currentRows }}
                    isSorted
                    canSearch
                    showTotalEntries
                    entriesPerPage={{
                      defaultValue: 10,
                      entries: [5, 10, 25, 50, 100],
                    }}
                    pagination={{ variant: "gradient", color: "info" }}
                    noEndBorder={false}
                  />
                </MDBox>
              </Card>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AnnotatorExamsIndex;
